import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ChevronRight, type LucideIcon } from "lucide-react"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { CALCULATOR_GROUPS } from "./-groups"

export const Route = createFileRoute("/calculators/")({
  component: Calculators,
})

function Calculators() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  return (
    <>
      <Header title="Calculators" showPeriod={false} />
      <PageWrapper>{isDesktop ? <CalculatorGrid /> : <CalculatorList />}</PageWrapper>
    </>
  )
}

function IconTile({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-green/10 text-accent-green">
      <Icon className="size-4" aria-hidden />
    </span>
  )
}

/** Phone: an iOS-style grouped list, like /settings and /more. */
function CalculatorList() {
  return (
    <div data-testid="calculators-list" className="mx-auto w-full max-w-lg space-y-6">
      {CALCULATOR_GROUPS.map((group) => (
        <section key={group.label} aria-labelledby={`calc-group-${group.label}`}>
          <h2
            id={`calc-group-${group.label}`}
            className="mb-2 px-1 text-label font-semibold uppercase tracking-wider text-text-hint"
          >
            {group.label}
          </h2>
          <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
            {group.items.map((calc, i) => (
              <Link
                key={calc.to}
                to={calc.to}
                className={
                  "flex min-h-12 items-center gap-3 px-4 py-3 transition-colors hover:bg-bg-elevated" +
                  (i > 0 ? " border-t border-border-subtle" : "")
                }
              >
                <IconTile icon={calc.icon} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-callout text-text-primary">{calc.label}</span>
                  <span className="truncate text-caption text-text-muted">{calc.description}</span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-text-hint" aria-hidden />
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

/** Desktop: one card per calculator, its group as an eyebrow. */
function CalculatorGrid() {
  return (
    <div
      data-testid="calculators-grid"
      className="mx-auto grid w-full max-w-5xl grid-cols-2 gap-4 lg:grid-cols-3"
    >
      {CALCULATOR_GROUPS.flatMap((group) =>
        group.items.map((calc) => (
          <Link
            key={calc.to}
            to={calc.to}
            className="group flex min-w-0 flex-col gap-3 rounded-xl bg-bg-surface p-5 ring-1 ring-border-subtle transition-colors hover:bg-bg-elevated hover:ring-border-default"
          >
            <div className="flex items-center justify-between gap-2">
              <IconTile icon={calc.icon} />
              <span className="text-label font-semibold uppercase tracking-wider text-text-hint">
                {group.label}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-callout font-semibold text-text-primary">{calc.label}</span>
              <span className="text-body text-text-muted">{calc.description}</span>
            </div>
          </Link>
        )),
      )}
    </div>
  )
}
